"use client";

import { useEffect, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { at, useReveal } from "./motion";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function remaining(target: number) {
  const left = Math.max(0, target - Date.now());
  return [
    Math.floor(left / DAY),
    Math.floor((left % DAY) / HOUR),
    Math.floor((left % HOUR) / MINUTE),
    Math.floor((left % MINUTE) / SECOND),
  ];
}

export const pad = (n: number) => String(n).padStart(2, "0");

/** [days, hours, minutes, seconds] left until `target` (ISO), ticking every second; zeros once past. */
export function useCountdown(target?: string) {
  const targetMs = target ? Date.parse(target) : NaN;
  const [parts, setParts] = useState(() => (Number.isNaN(targetMs) ? [0, 0, 0, 0] : remaining(targetMs)));
  useEffect(() => {
    if (Number.isNaN(targetMs)) return;
    const tick = () => setParts(remaining(targetMs));
    tick();
    const id = window.setInterval(tick, SECOND);
    return () => window.clearInterval(id);
  }, [targetMs]);
  return parts;
}

/** The hero and final CTA unit labels, by page language. Figma's EN frame has German ones, so they live here, not in the CMS. */
const UNIT_LABELS: Record<string, [string, string, string, string]> = {
  en: ["Days", "Hours", "Minutes", "Seconds"],
  de: ["Tage", "Stunden", "Minuten", "Sekunden"],
  br: ["Dias", "Horas", "Minutos", "Segundos"],
};

export const unitLabelsFor = (language?: string) =>
  UNIT_LABELS[stegaClean(language) || DEFAULT_LANGUAGE] ?? UNIT_LABELS[DEFAULT_LANGUAGE];

/** Figma's column widths of the hero card (days, hours, minutes, seconds): the row never shifts as digits change. */
const HERO_COLUMNS = [51, 68, 65, 77];

interface Props {
  label?: string;
  target?: string;
  unitLabels: (string | undefined)[];
  /**
   * `dark`: green card with a progress segment (hero). `glass`: the same over a photo, on a 19%
   * smoke (final CTA). `light`: white card, plain grey rule (leaderboard).
   */
  variant?: "dark" | "glass" | "light";
  /** Column widths in Figma px. */
  columns?: number[];
  className?: string;
  /** Entrance: the card rises at this delay (s) and its progress line paints in after it. */
  revealDelay?: number;
  /** Be its own reveal scope (plays when scrolled into view) instead of following a parent's. */
  ownReveal?: boolean;
}

/**
 * "The board goes live in" card. Every size is an `em` of the card's own font-size, which is one
 * Figma px: 0.125rem on desktop (452px card) and 0.1025rem on mobile, where Figma scales the same
 * card by 0.82 (370.64px).
 */
export default function TradingCompetitionCountdown({
  label,
  target,
  unitLabels,
  variant = "dark",
  columns = HERO_COLUMNS,
  className,
  revealDelay,
  ownReveal = false,
}: Props) {
  const light = variant === "light";
  const parts = useCountdown(target);
  // The digits are pre-rendered at build time and hydrate with suppressHydrationWarning,
  // so React keeps the server text when it differs. Once the target has passed every
  // tick computes the same zeros, React sees no change, and the stale build-time digits
  // would stay on screen forever. Re-keying them after mount forces a real client render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const [revealRef, revealState] = useReveal({ threshold: 0.4 });
  const animated = revealDelay !== undefined;

  return (
    <div
      ref={ownReveal ? revealRef : undefined}
      data-reveal={ownReveal ? revealState : undefined}
      style={animated ? { ...at(revealDelay, 1), ["--rise" as string]: "3rem" } : undefined}
      role="timer"
      aria-label={label}
      className={cn(
        animated && "tc-rv-rise",
        "absolute h-[175em] w-[452em] rounded-[4em] border-[0.874em] border-solid border-white/30 text-[0.1025rem] lg:text-[0.125rem]",
        light
          ? "bg-white text-black"
          : variant === "glass"
            ? // Figma's Glass (radius 30) as a backdrop blur of half the radius
              "bg-[rgba(22,22,22,0.19)] text-white backdrop-blur-[15em]"
            : "bg-[#03261d] text-white",
        className,
      )}>
      <div className="absolute top-[23.13em] right-[23.13em] left-[23.13em] flex h-[22em] items-start justify-center">
        <p className="mb-0 font-geist text-[16.594em] leading-[normal] font-normal whitespace-nowrap uppercase opacity-70">
          {label}
        </p>
      </div>

      {/* Figma's "Line bar": grey track; the dark card adds a white progress segment, 0.35px lower */}
      {light ? (
        <i aria-hidden="true" className="absolute top-[53.01em] left-[23.13em] h-[1em] w-[400em] bg-[#989898] opacity-40" />
      ) : (
        <>
          <i aria-hidden="true" className="absolute top-[52.51em] left-[23.13em] h-[2em] w-[400em] bg-[#989898] opacity-40" />
          <i
            aria-hidden="true"
            className={cn("absolute top-[52.862em] left-[23.13em] h-[2em] w-[118.994em] bg-white", animated && "tc-rv-paint")}
            style={animated ? at(revealDelay + 0.6, 1.1) : undefined}
          />
        </>
      )}

      <div className="absolute top-[62.6em] left-[23.13em] flex items-center gap-[22em] pb-[8em] text-center leading-[normal]">
        {parts.map((value, i) => (
          <div key={i} className="contents">
            {i > 0 && (
              <p className="mb-0 font-helvetica-lt text-[23.718em] font-medium whitespace-nowrap opacity-70">:</p>
            )}
            <div className="flex flex-col items-center gap-[5.93em]" style={{ width: `${columns[i]}em` }}>
              <p
                key={mounted ? "client" : "server"}
                suppressHydrationWarning
                className="mb-0 w-full font-geist text-[48em] font-medium whitespace-nowrap">
                {pad(value)}
              </p>
              <p className="mb-0 w-full font-geist text-[14.52em] font-normal whitespace-nowrap uppercase opacity-70">
                {unitLabels[i]}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
